import { useEffect, useState } from 'react'

import { Box, Button, IconButton, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons'
import { range, isEqual } from 'lodash'

import { MultiSectionDialog } from '@scoir/modals'

import CharacterLimitInput from 'routes/content-management/ContentManagement/components/CharacterLimitInput'
import ModalError from '../../ModalError'

const useStyles = makeStyles((theme) => ({
  textFields: {
    padding: theme.spacing(2, 0, 2, 2),
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(3),
  },
}))

interface EditHighlightsModalProps {
  hasSaveError?: boolean
  highlights: string[] | undefined
  isOpen: boolean
  onClose: () => void
  onSave: (finalHighlights: string[]) => void
}

const maxPoints = 5
type Points = { value: string; order: number }

const EditHighlightsModal = ({ hasSaveError, highlights, isOpen, onClose, onSave }: EditHighlightsModalProps) => {
  const classes = useStyles()
  const [hasCharacterError, setHasCharacterError] = useState(false)

  const getDefaultPoints = (highlights: string[] | undefined, maxPoints: number) =>
    range(0, maxPoints).map((idx) => ({
      value: highlights?.[idx] || '',
      order: idx,
    }))
  const [defaultPoints, setDefaultPoints] = useState<Points[]>([])
  const [points, setPoints] = useState<Points[]>([])

  useEffect(() => {
    setPoints(getDefaultPoints(highlights, maxPoints))
    setDefaultPoints(getDefaultPoints(highlights, maxPoints))
  }, [highlights])

  const handleMove = (idx: number, direction: number) => {
    const newPoints = [...points]
    const point = newPoints[idx]
    newPoints[idx] = newPoints[idx + direction]
    newPoints[idx + direction] = point
    setPoints(newPoints)
  }
  const handleOnClose = () => {
    onClose()
  }
  const handleSave = () => {
    const finalHighlights = points.map((point) => point.value).filter((value) => value !== '')
    onSave(finalHighlights)
  }
  return (
    <MultiSectionDialog
      open={isOpen}
      onClose={onClose}
      scroll="paper"
      headerText="Edit Highlights"
    >
      <MultiSectionDialog.Body>
        <Box
          display="flex"
          flexDirection="column"
          gridGap={16}
        >
          <Typography>
            What makes your school special? Add five short sentences to describe unique characteristics and value
            proposition on your profile. Hint: Try not to use your institution name.
          </Typography>
          <Paper
            elevation={0}
            className={classes.textFields}
          >
            {points.map((point, idx) => (
              <Box
                display="flex"
                gridGap={8}
                key={idx}
              >
                <CharacterLimitInput
                  value={point.value}
                  field={'Highlight'}
                  label={`Highlight ${idx + 1}`}
                  placeholder={`Highlight ${idx + 1}`}
                  charLimit={100}
                  id={`highlight-${idx}`}
                  onUpdateField={(_, value) => {
                    const newPoints = [...points]
                    newPoints[idx] = { ...newPoints[idx], value }
                    setPoints(newPoints)
                  }}
                  setError={setHasCharacterError}
                />
                <Box>
                  <IconButton
                    size="small"
                    disabled={idx === 0}
                    onClick={() => handleMove(idx, -1)}
                    data-testid={`move-up-${idx}`}
                    aria-label="Move Up"
                  >
                    <FontAwesomeIcon
                      icon={faAngleUp}
                      size="sm"
                    />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={idx === points.length - 1}
                    onClick={() => handleMove(idx, 1)}
                    data-testid={`move-down-${idx}`}
                    aria-label="Move Down"
                  >
                    <FontAwesomeIcon
                      icon={faAngleDown}
                      size="sm"
                    />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Paper>
        </Box>
      </MultiSectionDialog.Body>
      <MultiSectionDialog.Footer>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
        >
          {hasSaveError && <ModalError />}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              variant="text"
              color="primary"
              onClick={handleOnClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isEqual(points, defaultPoints) || hasCharacterError}
              onClick={handleSave}
            >
              Save & Publish
            </Button>
          </Box>
        </Box>
      </MultiSectionDialog.Footer>
    </MultiSectionDialog>
  )
}

export default EditHighlightsModal
